import React, { useState, useRef, useMemo } from 'react';
import { Upload, Check, ImagePlus, Trash2 } from 'lucide-react';
import axios from 'axios';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import UserAvatar from './UserAvatar';
import {
  AVATAR_LIST,
  AVATAR_CATEGORIES,
  AVATAR_CATEGORY_LABELS,
  getAvatarCategory,
} from './BookwormAvatars';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

/**
 * AvatarPicker — embedded in Settings page.
 *
 * Shows the current avatar, the bookworm preset gallery, and an "upload your
 * own photo" tile. Selection persists immediately via the avatar API and is
 * mirrored in localStorage so other pages render the new avatar without a
 * full reload.
 */
const AvatarPicker = ({ token, userName, currentAvatarId, currentAvatarPhotoUrl, onChange }) => {
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  const [avatarId, setAvatarId] = useState(currentAvatarId || null);
  const [avatarPhotoUrl, setAvatarPhotoUrl] = useState(currentAvatarPhotoUrl || null);
  const [busy, setBusy] = useState(false);
  // Auto-open the tab matching the current avatar so the user lands on it
  const [activeCategory, setActiveCategory] = useState(() =>
    currentAvatarId ? getAvatarCategory(currentAvatarId) : 'classic'
  );

  const groupedAvatars = useMemo(() => {
    const groups = { classic: [], disney: [], marvel: [] };
    AVATAR_LIST.forEach((a) => {
      const cat = AVATAR_CATEGORIES[a.id] || 'classic';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(a);
    });
    return groups;
  }, []);
  const visibleAvatars = groupedAvatars[activeCategory] || [];

  const persistLocal = (id, photo) => {
    try {
      // Mirror under both naming conventions so any consumer (camelCase or
      // snake_case) sees the latest selection without needing a re-login.
      if (id) {
        localStorage.setItem('userAvatarId', id);
        localStorage.setItem('avatar_id', id);
      } else {
        localStorage.removeItem('userAvatarId');
        localStorage.removeItem('avatar_id');
      }
      if (photo) {
        localStorage.setItem('userAvatarPhotoUrl', photo);
        localStorage.setItem('avatar_photo_url', photo);
      } else {
        localStorage.removeItem('userAvatarPhotoUrl');
        localStorage.removeItem('avatar_photo_url');
      }
    } catch {
      // ignore storage errors
    }
    if (onChange) onChange({ avatarId: id, avatarPhotoUrl: photo });
  };

  const handleSelectPreset = async (id) => {
    if (busy || id === avatarId) return;
    setBusy(true);
    try {
      const res = await axios.put(
        `${API}/user/me/avatar`,
        { avatar_id: id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newId = res.data?.avatar_id || id;
      setAvatarId(newId);
      setAvatarPhotoUrl(null);
      persistLocal(newId, null);
      toast({ title: 'Avatar updated', description: `You're now reading as a bookworm.` });
    } catch (e) {
      toast({
        title: 'Could not update avatar',
        description: e.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleUploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || busy) return;
    if (file.size > 2 * 1024 * 1024) {
      toast({ title: 'Photo too large', description: 'Max 2 MB', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/user/me/avatar/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      const newPhoto = res.data?.avatar_photo_url;
      setAvatarPhotoUrl(newPhoto);
      setAvatarId(null);
      persistLocal(null, newPhoto);
      toast({ title: 'Photo uploaded' });
    } catch (err) {
      toast({
        title: 'Upload failed',
        description: err.response?.data?.detail || 'Please try a smaller image',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleClear = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/user/me/avatar`, { headers: { Authorization: `Bearer ${token}` } });
      setAvatarId(null);
      setAvatarPhotoUrl(null);
      persistLocal(null, null);
      toast({ title: 'Avatar cleared' });
    } catch {
      toast({ title: 'Could not clear avatar', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="bg-white/5 backdrop-blur-sm rounded-lg p-6 border border-white/10"
      data-testid="avatar-picker-section"
    >
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <ImagePlus className="h-5 w-5 text-fuchsia-400" />
            Your Bookworm Avatar
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Pick a bookworm character or upload your own photo. It appears on every review,
            book discussion and chat message.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <UserAvatar
            name={userName}
            avatarId={avatarId}
            avatarPhotoUrl={avatarPhotoUrl}
            size="xl"
            ringColor="ring-fuchsia-400/40"
            testId="avatar-current-preview"
          />
        </div>
      </div>

      {/* Category tabs */}
      <div
        className="flex items-center gap-1 mb-4 bg-black/30 p-1 rounded-lg w-fit"
        data-testid="avatar-category-tabs"
      >
        {Object.keys(AVATAR_CATEGORY_LABELS).map((cat) => {
          const count = (groupedAvatars[cat] || []).length;
          const active = activeCategory === cat;
          return (
            <button
              key={cat}
              type="button"
              onClick={() => setActiveCategory(cat)}
              data-testid={`avatar-category-${cat}`}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                active
                  ? 'bg-fuchsia-500 text-white shadow-sm'
                  : 'text-gray-300 hover:bg-white/10'
              }`}
            >
              {AVATAR_CATEGORY_LABELS[cat]}
              <span className="ml-1.5 text-[10px] opacity-70">({count})</span>
            </button>
          );
        })}
      </div>

      {/* Preset gallery */}
      <div
        className="grid grid-cols-4 sm:grid-cols-6 gap-3 mb-6"
        data-testid="avatar-preset-gallery-wrapper"
      >
        {visibleAvatars.map((preset) => {
          const selected = avatarId === preset.id && !avatarPhotoUrl;
          return (
            <button
              key={preset.id}
              onClick={() => handleSelectPreset(preset.id)}
              disabled={busy}
              type="button"
              className={`relative group rounded-xl p-1.5 transition-all ${
                selected
                  ? 'bg-fuchsia-500/20 ring-2 ring-fuchsia-400'
                  : 'bg-white/5 hover:bg-white/10 ring-1 ring-white/10 hover:ring-white/30'
              }`}
              title={preset.label}
              data-testid={`avatar-preset-${preset.id}`}
            >
              <div className="w-full aspect-square">
                <UserAvatar
                  name={preset.label}
                  avatarId={preset.id}
                  size={64}
                  className="w-full h-full"
                />
              </div>
              <p className="text-[10px] text-gray-300 mt-1.5 text-center font-medium truncate">
                {preset.label.split(' ')[0]}
              </p>
              {selected && (
                <span className="absolute top-1 right-1 bg-fuchsia-500 text-white rounded-full p-0.5">
                  <Check className="h-3 w-3" />
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Upload + clear actions */}
      <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-white/10">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleUploadFile}
          data-testid="avatar-upload-input"
        />
        <Button
          type="button"
          variant="secondary"
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
          data-testid="avatar-upload-btn"
          className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
        >
          <Upload className="mr-2 h-4 w-4" /> Upload custom photo
        </Button>
        {(avatarId || avatarPhotoUrl) && (
          <Button
            type="button"
            variant="ghost"
            onClick={handleClear}
            disabled={busy}
            data-testid="avatar-clear-btn"
            className="text-gray-300 hover:text-white"
          >
            <Trash2 className="mr-2 h-4 w-4" /> Clear avatar
          </Button>
        )}
        <span className="text-xs text-gray-500 ml-auto">PNG/JPG up to 2&nbsp;MB</span>
      </div>
    </div>
  );
};

export default AvatarPicker;
